import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { ValuesSection } from '../components/ValuesSection';
import { CoachCard, Coach } from '../components/CoachCard';
import { CoachModal } from '../components/CoachModal';
import { coaches } from '../data/coaches';
export function AboutPage() {
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  return <main className="bg-white pt-20" data-id="element-284">
      {/* 1. HERO SECTION */}
      <section className="relative overflow-hidden bg-brand-blue pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px]" data-id="element-285">
        <img src="https://cdn.magicpatterns.com/uploads/oReLAPLbn28EHKH4dsYqgd/Beijing-2008-Opening-Ceremony-DK-Photo-1-scaled.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-286" />
        <div className="absolute inset-0 bg-brand-blue/80" data-id="element-287"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-288">
          <nav aria-label="Breadcrumb" className="flex text-sm font-medium text-white/80 mb-8" data-id="element-289">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-290">
              Home
            </Link>
            <span className="mx-2" data-id="element-291">/</span>
            <span className="text-white" data-id="element-292">About</span>
          </nav>

          <div className="max-w-3xl" data-id="element-293">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-294">
              Olympic-Level Coaching for{' '}
              <span className="text-brand-yellow" data-id="element-295">Lifelong Success</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-296">
              Master FUNdamental motor skills in an inspiring, teamwork-driven
              environment, guided by our lead coaches Donna Kellogg MBE, Natalie
              Barton and Sarah Chapman, all Team GB Olympians.
            </p>
          </div>
        </div>
      </section>

      {/* 2. WHY BRIGHT STAR SPORTS SECTION */}
      <section className="py-24 bg-white" data-id="element-297">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-298">
          <div data-id="element-299">
            <SectionHeader eyebrow="Why Bright Star Sports?" title="The Gold Standard in Youth Coaching" align="left" data-id="element-300" />
            <ul className="space-y-4 text-navy-600 text-lg" data-id="element-301">
              {['Led by Team GB Olympians and world championship medallists', 'Structured programmes designed for ages 3 to 16', 'Focus on FUNdamental motor skills, confidence, and teamwork', 'Inclusive sessions welcoming all abilities and experience levels', 'Delivered in schools, sports centres, and community venues across the UK'].map((item, i) => <li key={i} className="flex items-start" data-id="element-302">
                  <CheckIcon className="w-6 h-6 text-brand-red mr-3 flex-shrink-0 mt-0.5" data-id="element-303" />
                  <span data-id="element-304">{item}</span>
                </li>)}
            </ul>
          </div>
          <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden border border-navy-100" data-id="element-305">
            <img src="https://cdn.magicpatterns.com/uploads/oReLAPLbn28EHKH4dsYqgd/Beijing-2008-Opening-Ceremony-DK-Photo-1-scaled.jpg" alt="Team GB at the Beijing 2008 Olympic Opening Ceremony" className="w-full h-full object-cover" data-id="element-306" />
          </div>
        </div>
      </section>

      {/* 3. CORE VALUES SECTION */}
      <ValuesSection data-id="element-307" />

      {/* 4. TEAM SECTION */}
      <section className="py-24 bg-white" data-id="element-308">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-309">
          <SectionHeader eyebrow="Our Team" title="Meet the Team" description="Our lead coaches Donna Kellogg MBE, Natalie Barton and Sarah Chapman are all Team GB Olympians dedicated to inspiring the next generation through sport." data-id="element-310" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-311">
            {coaches.map((coach, index) => <CoachCard key={coach.id} coach={coach} index={index} onViewProfile={setSelectedCoach} data-id="element-312" />)}
          </div>
        </div>
      </section>

      {/* 5. DUAL CTA SECTION */}
      <section className="py-16 bg-navy-50" data-id="element-313">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-314">
          <div className="bg-brand-yellow rounded-2xl p-8 md:p-10 flex flex-col items-start" data-id="element-315">
            <h3 className="text-2xl md:text-3xl font-display font-bold text-navy-900 mb-4" data-id="element-316">
              Train Like Champions
            </h3>
            <p className="text-lg text-navy-800 mb-8 flex-1" data-id="element-317">
              Join our Olympian-led sessions and unlock your child's potential
              in a fun, supportive environment.
            </p>
            <Button href="/book" variant="primary" aria-label="Book a coaching session" data-id="element-318">
              Book a Session
            </Button>
          </div>

          <div className="bg-navy-900 rounded-2xl p-8 md:p-10 flex flex-col items-start" data-id="element-319">
            <h3 className="text-2xl md:text-3xl font-display font-bold text-white mb-4" data-id="element-320">
              Coach with Olympians
            </h3>
            <p className="text-lg text-navy-200 mb-8 flex-1" data-id="element-321">
              Join our team of passionate coaches and make a difference in the
              lives of young athletes.
            </p>
            <Button href="/become-a-coach" variant="secondary" className="border-white text-white hover:bg-white/20 focus:ring-white focus:ring-offset-navy-900 focus:ring-offset-2" aria-label="Learn more about coaching opportunities" data-id="element-322">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Coach Modal */}
      <CoachModal coach={selectedCoach} isOpen={!!selectedCoach} onClose={() => setSelectedCoach(null)} data-id="element-323" />
    </main>;
}